import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../services/apiBase';
import { getStoredToken, signOut } from '../services/authSession';

// Create the context
const ClientContext = createContext(null);

// Custom hook for using client context
export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

// Provider component
export const ClientProvider = ({ children }) => {
  const [activeClient, setActiveClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearSessionAndRedirect = useCallback(() => {
    signOut();
  }, []);

  const restoreClientFromUserScope = useCallback(async (currentToken) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userClientId = user?.client_id;
      if (!userClientId) {
        return false;
      }

      const response = await fetch(`${API}/clients/${userClientId}`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      if (!response.ok) {
        return false;
      }

      const client = await response.json();
      if (!client?.id) {
        return false;
      }

      setClients([client]);
      setActiveClient(client);
      localStorage.setItem('active_client_id', client.id);
      localStorage.setItem('active_client_name', client.name || client.display_name || client.id);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Load the clients this user may work on: every client for a super admin,
  // the assignment list for a consultant, their own client for client roles.
  const loadClients = useCallback(async () => {
    const currentToken = getStoredToken();
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/my-clients`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Prefer active clients; if none are active yet, allow non-inactive clients
        // so the UI doesn't become blocked during onboarding transitions.
        let selectableClients = data.filter(c => c.status === 'active');
        if (selectableClients.length === 0) {
          selectableClients = data.filter(c => c.status !== 'inactive');
        }
        setClients(selectableClients);

        // Restore previously selected client from localStorage
        const savedClientId = localStorage.getItem('active_client_id');
        if (savedClientId) {
          const savedClient = selectableClients.find(c => c.id === savedClientId);
          if (savedClient) {
            setActiveClient(savedClient);
          } else if (selectableClients.length > 0) {
            // If saved client not found, select first active client
            setActiveClient(selectableClients[0]);
            localStorage.setItem('active_client_id', selectableClients[0].id);
          }
        } else if (selectableClients.length > 0) {
          // No saved client, select first one
          setActiveClient(selectableClients[0]);
          localStorage.setItem('active_client_id', selectableClients[0].id);
        }
      } else if (response.status === 403) {
        // Non-super-admins may not list all clients; restore scoped client from user.
        const restored = await restoreClientFromUserScope(currentToken);
        if (!restored) {
          setError('Unable to load client context for current user');
        }
      } else if (response.status === 401) {
        clearSessionAndRedirect();
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clearSessionAndRedirect, restoreClientFromUserScope]);

  // Load clients on mount and when token changes
  useEffect(() => {
    // Poll for token on initial load (in case login just happened)
    const checkToken = () => {
      const currentToken = getStoredToken();
      if (currentToken) {
        loadClients();
        return true;
      }
      return false;
    };
    
    if (!checkToken()) {
      // If no token, wait a bit and try again (login might be in progress)
      const timeout = setTimeout(() => {
        checkToken();
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [loadClients]);

  // Switch to a different client
  const switchClient = useCallback((client) => {
    setActiveClient(client);
    localStorage.setItem('active_client_id', client.id);
    localStorage.setItem('active_client_name', client.name);
    
    // Dispatch custom event so components can react to client change
    window.dispatchEvent(new CustomEvent('clientChanged', { detail: client }));
  }, []);

  // Refresh clients list
  const refreshClients = useCallback(() => {
    loadClients();
  }, [loadClients]);

  // Get the active client ID (convenience method)
  const getClientId = useCallback(() => {
    return activeClient?.id || null;
  }, [activeClient]);

  // Check if a client is selected
  const hasActiveClient = useCallback(() => {
    return activeClient !== null;
  }, [activeClient]);

  const value = {
    // State
    activeClient,
    clients,
    loading,
    error,
    
    // Actions
    switchClient,
    refreshClients,
    
    // Helpers
    getClientId,
    hasActiveClient,
    
    // Convenience getters
    clientId: activeClient?.id || null,
    clientName: activeClient?.name || 'No Client Selected',
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

export default ClientContext;
